import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
// Side-effect: registers <schmancy-visually-hidden> — the day headers' spoken
// date, the same explicit import programme-day-selector.view.ts carries.
import "@mhmo91/schmancy/visually-hidden";
import type { Handover } from "@momo/types";
import { Ticket } from "@momo/types";
import { html } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	catchError,
	defer,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	merge,
	mergeMap,
	type Observable,
	of,
	Subject,
	take,
	takeUntil,
	tap,
} from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import type { TicketTypeRow } from "../ticket-type-row.view.ts";
// Side-effect: registers the production <ticket-type-row> this picker mounts —
// the identical row the storefront checkout drives.
import "../ticket-type-row.view.ts";
import {
	type HandoverPickerRow,
	type HandoverSettlement,
	type HeldHandover,
	handoverBook,
	handoverNames,
	handoverPickerRows,
	handoverSettled$,
	handTicketsOver,
	resolveGroupPick,
	takeTicketsBack,
	ticketsTakenBack$,
} from "./handover.state.ts";
// Side-effect: registers <handover-email-address>, the overlay the E-Mail
// exit opens over this sheet.
import "./handover-email-address.view.ts";
import {
	composeHandoverMessage,
	openHandoverMessage,
} from "./handover-message.ts";
import { orderAccessEvent } from "./order-access.state.ts";

@schmancyElement("handover-sheet")
export class HandoverSheet extends LocalizeMixin(SchmancyElement) {
	/** The ticket whose control was tapped — preselected, never asked about
	 *  again. Null when the sheet opens on an existing handover. */
	@property({ attribute: false }) seedTicketId: string | null = null;

	/** Set when opened from an already-given ticket's own answer — prefills name
	 *  and tickets and adds the take-back exit. */
	@property({ attribute: false }) editDraftId: string | null = null;

	@state() private name = "";

	@state() private picked: readonly string[] = [];

	/** Which sending exit is waiting for its link — the sheet's one busy
	 *  state, since only one give can be in flight per sheet. */
	@state() private sending:
		| Handover.HandoverChannel
		| "record"
		| "reclaim"
		| null = null;

	private pendingRows: readonly HandoverPickerRow[] | null = null;
	private pendingNames: readonly string[] | null = null;
	private pendingEditing: HeldHandover | null = null;
	private reclaim = new Subject<string>();

	/** Whether the ticket picker stands open. Closed on every path the sheet
	 *  opens on, an already-given ticket included: the header above it names every
	 *  ticket going, and its own summary carries the live count, so nothing a
	 *  collapsed picker holds is hidden — and the rows are one tap away. Never
	 *  re-derived from `picked` afterwards, so a buyer who opened the picker and
	 *  stepped back down to one ticket does not have it shut under their hand. */
	@state() private pickerOpen = false;

	private deliver = new Subject<{
		readonly draftId: string;
		readonly channel: Handover.HandoverChannel | null;
		readonly settle:
			| ((accepted: boolean, recorded: boolean) => void)
			| undefined;
		readonly ready?: boolean;
	}>();

	@state() private failed:
		| Handover.HandoverChannel
		| "record"
		| "reclaim"
		| null = null;

	override willUpdate() {
		if (this.hasUpdated) return;
		const editing = handoverBook.value.find(
			(held) => held.draftId === this.editDraftId,
		);
		this.name = editing?.name ?? "";
		this.picked = editing
			? [...editing.ticketIds]
			: this.seedTicketId
				? [this.seedTicketId]
				: [];
	}

	override connectedCallback() {
		super.connectedCallback();
		merge(
			this.deliver.pipe(
				exhaustMap(({ draftId, channel, settle, ready }) => {
					let accepted = false;
					let recorded = false;
					let withdrawn = false;
					let nextDraftId = this.editDraftId;
					const settlement: Observable<HandoverSettlement> = ready
						? of({ draftId, outcome: "landed", mailAccepted: null })
						: handoverSettled$;
					return settlement.pipe(
						filter((settled) => settled.draftId === draftId),
						take(1),
						mergeMap((settled) => {
							// Neither landed nor refused as far as this page knows: the
							// sheet closes and says nothing about the give.
							if (settled.outcome === "withdrawn") {
								withdrawn = true;
								return EMPTY;
							}
							const held = handoverBook.value.find(
								(candidate) => candidate.draftId === draftId,
							);
							const event = orderAccessEvent.get();
							if (settled.outcome === "refused" || !held?.url) {
								this.failed = channel ?? "record";
								return EMPTY;
							}
							nextDraftId = held.draftId;
							recorded = true;
							if (settled.mailAccepted === false) {
								this.failed = "email";
								return EMPTY;
							}
							if (channel === null || channel === "email") {
								accepted = true;
								return of(null);
							}
							if (event === null) {
								this.failed = channel;
								return EMPTY;
							}
							return openHandoverMessage(
								channel,
								composeHandoverMessage({
									uiLanguage: this.uiLanguage,
									formattingLocale: this.locale,
									carrier: channel,
									recipient: held.name,
									event,
									tickets: (this.pendingRows ?? handoverPickerRows.get())
										.filter((row) =>
											held.ticketIds.includes(row.ticket.ticketId),
										)
										.map((row) => row.ticket),
									url: held.url,
								}),
							).pipe(
								tap((delivery) => {
									accepted = delivery === "opened";
								}),
								catchError((err: unknown) => {
									console.error(
										"[handover-sheet] the carrier refused the message",
										err,
									);
									this.failed = channel;
									return EMPTY;
								}),
							);
						}),
						finalize(() => {
							this.editDraftId = nextDraftId;
							this.sending = null;
							this.pendingRows = null;
							this.pendingNames = null;
							this.pendingEditing = null;
							// A withdrawn give closes the address form without a failure
							// line, since none is known.
							settle?.(accepted || withdrawn, recorded);
							if ((accepted || withdrawn) && this.isConnected)
								this.dispatchEvent(
									new CustomEvent("close", { bubbles: true, composed: true }),
								);
						}),
					);
				}),
			),
			this.reclaim.pipe(
				exhaustMap((handoverId) =>
					defer(() => {
						return merge(
							ticketsTakenBack$.pipe(
								filter((settled) => settled.handoverId === handoverId),
							),
							defer(() => {
								takeTicketsBack(handoverId);
								return EMPTY;
							}),
						).pipe(
							take(1),
							tap(({ outcome }) => {
								if (outcome !== "refused") {
									this.dispatchEvent(
										new CustomEvent("close", { bubbles: true, composed: true }),
									);
								} else this.failed = "reclaim";
							}),
							finalize(() => {
								this.sending = null;
								this.pendingRows = null;
								this.pendingNames = null;
								this.pendingEditing = null;
							}),
						);
					}),
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private submit(
		channel: Handover.HandoverChannel | null,
		recipientEmail: string | null,
		settle?: (accepted: boolean, recorded: boolean) => void,
	): void {
		if (this.sending !== null || !this.name.trim() || this.picked.length === 0)
			return;
		this.pendingRows = handoverPickerRows.get();
		this.pendingNames = handoverNames.get();
		this.pendingEditing =
			handoverBook.value.find((held) => held.draftId === this.editDraftId) ??
			null;
		this.sending = channel ?? "record";
		this.failed = null;
		if (
			channel !== "email" &&
			this.pendingEditing?.url &&
			this.pendingEditing.name === this.name.trim() &&
			this.pendingEditing.ticketIds.length === this.picked.length &&
			this.picked.every((id) => this.pendingEditing?.ticketIds.includes(id))
		) {
			this.deliver.next({
				draftId: this.pendingEditing.draftId,
				channel,
				settle,
				ready: true,
			});
			return;
		}
		const draftId = handTicketsOver({
			name: this.name.trim(),
			ticketIds: [...this.picked],
			sentVia: channel,
			recipientEmail,
		});
		this.deliver.next({ draftId, channel, settle });
	}

	override render() {
		// The picker offers a ticket that is still this page's own, or one
		// already part of THIS handover — a ticket given to someone else is
		// changed from its own card's "Ändern", never silently pulled over here.
		const rows = this.pendingRows ?? handoverPickerRows.get();
		const offered = rows.filter(
			(row) =>
				row.handover === null ||
				(row.handover.draftId === this.editDraftId && row.handover.reclaimable),
		);
		// One picker row per (programme day, type), in the order's own order —
		// two types sharing a display name on different days stay two rows,
		// which is the day tile's whole job to disambiguate.
		const groups: {
			readonly key: string;
			readonly day: string | null;
			readonly typeName: string;
			readonly ticketIds: string[];
		}[] = [];
		for (const row of offered) {
			const key = `${row.programmeDay ?? "*"}|${row.ticket.typeName}`;
			const found = groups.find((group) => group.key === key);
			if (found) found.ticketIds.push(row.ticket.ticketId);
			else
				groups.push({
					key,
					day: row.programmeDay,
					typeName: row.ticket.typeName,
					ticketIds: [row.ticket.ticketId],
				});
		}
		const days = [...new Set(groups.map((group) => group.day))].sort((a, b) =>
			a === null ? 1 : b === null ? -1 : a.localeCompare(b),
		);
		// The chrome exists only where it decides something: one day (or one
		// whole-event band) renders as a bare list.
		const grouped = days.length > 1;
		const suggestions = (this.pendingNames ?? handoverNames.get()).filter(
			(name) => name !== this.name.trim(),
		);
		const editing =
			this.sending !== null
				? this.pendingEditing
				: handoverBook.value.find((held) => held.draftId === this.editDraftId);
		// What is going, in the order's own order: the tapped card's ticket by
		// type and display id while it is the only one, the per-type tally once
		// the pick grows past it — never a list of ids, which is the one thing
		// about a handover the buyer cannot check at a glance.
		const pickedRows = rows.filter((row) =>
			this.picked.includes(row.ticket.ticketId),
		);
		const single = pickedRows.length === 1 ? pickedRows[0] : undefined;
		const perType = new Map<string, number>();
		for (const row of pickedRows)
			perType.set(
				row.ticket.typeName,
				(perType.get(row.ticket.typeName) ?? 0) + 1,
			);
		const ticketsLine = single
			? this.t("storefront.orderAccess.handover.sheet.ticketsOne", {
					type: single.ticket.typeName,
					displayId: Ticket.ticketDisplayId(single.ticket.ticketId),
				})
			: pickedRows.length === 0
				? this.t("storefront.orderAccess.handover.sheet.ticketsNone")
				: [...perType]
						.map(([type, count]) =>
							this.t("storefront.orderAccess.handover.sheet.ticketsPerType", {
								count,
								type,
							}),
						)
						.join(" · ");
		const ready =
			this.sending === null &&
			this.name.trim().length > 0 &&
			this.picked.length > 0;
		return html`
			<!-- w-md is the anchored (desktop) measure; the viewport clamp is
			     against 100vw, not max-w-full, because the overlay's grid mount
			     is content-sized — a percentage there resolves against this
			     element's own measured width and clamps nothing. -->
			<div class="flex w-md max-w-[calc(100vw-1.5rem)] flex-col gap-6 p-5">
				<!-- The header names the subject before it asks anything: a buyer
				     who reached this sheet from one card among five must be able
				     to see, without opening the picker, exactly which tickets the
				     next tap sends. -->
				<div class="flex flex-col gap-1">
					<schmancy-typography type="headline" token="sm">
						${this.t("storefront.orderAccess.handover.sheet.title")}
					</schmancy-typography>
					<schmancy-typography type="body" token="md" class="text-surface-onVariant">
						${ticketsLine}
					</schmancy-typography>
				</div>

				<div ?inert=${this.sending !== null} class="flex flex-col gap-2">
					<schmancy-input
						label=${this.t("storefront.orderAccess.handover.sheet.nameLabel")}
						placeholder=${this.t("storefront.orderAccess.handover.sheet.namePlaceholder")}
						hint=${this.t("storefront.orderAccess.handover.sheet.nameHint")}
						autofocus
						.value=${this.name}
						@input=${(e: CustomEvent<{ value: string }>) => {
							if (this.sending !== null) return;
							this.name = e.detail.value;
						}}
					></schmancy-input>
					${when(
						suggestions.length > 0,
						() => html`
							<div class="flex flex-wrap gap-1">
								${repeat(
									suggestions,
									(name) => name,
									(name) => html`
										<schmancy-suggestion-chip
											elevated
											icon="person"
											@click=${() => {
												if (this.sending !== null) return;
												this.name = name;
											}}
										>
											${name}
										</schmancy-suggestion-chip>
									`,
								)}
							</div>
						`,
					)}
				</div>

				<!-- Progressive disclosure, not a standing question: the buyer
				     already named a ticket by tapping its card, so the picker opens
				     closed on every path — "Ändern" included, where the header
				     above already names every ticket the handover carries and this
				     summary counts them. A second ticket for the same person is the
				     opt-in, not the premise. -->
				<schmancy-details
					?inert=${this.sending !== null}
					type="containerLow"
					summary-padding="px-3 py-2"
					content-padding="px-1 pb-2 pt-1"
					?open=${this.pickerOpen}
					@toggle=${(e: CustomEvent<{ open: boolean }>) => {
						this.pickerOpen = e.detail.open;
					}}
				>
					<!-- The count and the invitation are the same two elements with
					     different text, never two branches: the icon and the label
					     keep their identity across the flip, so a ticket landing in
					     the pick swaps a word rather than rebuilding the summary
					     under the buyer's finger. -->
					<span slot="summary" class="flex items-center gap-2">
						<schmancy-icon>
							${pickedRows.length > 1 ? "confirmation_number" : "add"}
						</schmancy-icon>
						<schmancy-typography type="label" token="md">
							${
								// The summary carries the live count the moment the pick
								// is more than the tapped ticket: a control that folds a
								// non-default answer out of sight is one the buyer cannot
								// trust. At one ticket the header above has already named
								// it, so the summary is free to be the invitation.
								pickedRows.length > 1
									? this.t(
											"storefront.orderAccess.handover.sheet.pickerCount",
											{ count: pickedRows.length },
										)
									: this.t("storefront.orderAccess.handover.sheet.pickerAdd")
							}
						</schmancy-typography>
					</span>
					<div class="flex flex-col gap-4">
						${repeat(
							days,
							(day) => day ?? "*",
							(day) => {
								// The key IS the venue's calendar day, so it renders in UTC —
								// any other zone can shift it by one, which is the event
								// page's own programme-day rule.
								const calendar =
									day === null ? null : new Date(`${day}T00:00:00.000Z`);
								const datePart = (opts: Intl.DateTimeFormatOptions) =>
									calendar?.toLocaleDateString(this.locale, {
										...opts,
										timeZone: "UTC",
									}) ?? "";
								return html`
									<div class="flex flex-col gap-2">
										${when(grouped, () =>
											calendar === null
												? html`
														<div class="flex items-center gap-3 pt-1">
															<schmancy-typography
																type="label"
																token="sm"
																transform="uppercase"
																class="shrink-0 tracking-widest text-surface-onVariant"
															>
																${this.t("storefront.orderAccess.handover.sheet.wholeEvent")}
															</schmancy-typography>
															<schmancy-divider class="flex-1"></schmancy-divider>
														</div>
													`
												: html`
														<!-- The event page's own day grammar: the stacked
														     weekday/date/month tile its programme-day chips
														     carry, here at rest as a group heading rather
														     than a control. -->
														<div class="flex items-center gap-3 pt-1">
															<span class="flex w-9 shrink-0 flex-col items-center" aria-hidden="true">
																<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest text-surface-onVariant">
																	${datePart({ weekday: "short" })}
																</schmancy-typography>
																<schmancy-typography type="title" token="lg" class="tabular-nums">
																	${datePart({ day: "2-digit" })}
																</schmancy-typography>
																<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest text-surface-onVariant">
																	${datePart({ month: "short" })}
																</schmancy-typography>
															</span>
															<schmancy-divider class="flex-1"></schmancy-divider>
															<schmancy-visually-hidden>
																${datePart({ weekday: "long", day: "numeric", month: "long" })}
															</schmancy-visually-hidden>
														</div>
													`,
										)}
										${repeat(
											groups.filter((group) => group.day === day),
											(group) => group.key,
											(group) => {
												const held = group.ticketIds.length;
												const qty = this.picked.filter((id) =>
													group.ticketIds.includes(id),
												).length;
												// Tickets of this (day, type) with someone ELSE — the
												// reason the stepper caps below the order's own count.
												const elsewhere = new Map<string, number>();
												for (const row of rows)
													if (
														row.handover !== null &&
														row.handover.draftId !== this.editDraftId &&
														row.ticket.typeName === group.typeName &&
														row.programmeDay === group.day
													)
														elsewhere.set(
															row.handover.name,
															(elsewhere.get(row.handover.name) ?? 0) + 1,
														);
												// A day tile a sighted reader sees above the row is not
												// in the row's own accessible name, so a reader hearing
												// two rows of one type gets the day said out loud here.
												const spoken = !grouped
													? group.typeName
													: group.day === null
														? this.t(
																"storefront.orderAccess.handover.sheet.typeWholeEvent",
																{ type: group.typeName },
															)
														: this.t(
																"storefront.orderAccess.handover.sheet.typeOnDay",
																{
																	type: group.typeName,
																	date: datePart({
																		weekday: "short",
																		day: "numeric",
																		month: "short",
																	}),
																},
															);
												const row: TicketTypeRow = {
													id: group.key,
													name: group.typeName,
													description: null,
													// The row's second line is what a purchase would put
													// the price on: here, what is still the caller's to
													// give — which is also what caps the stepper.
													priceLabel: this.t(
														"storefront.orderAccess.handover.sheet.heldByYou",
														{ count: held },
													),
													hints: [...elsewhere].map(([holder, count]) => ({
														text: this.t(
															"storefront.orderAccess.handover.sheet.heldByOther",
															{ count, name: holder },
														),
													})),
													selected: qty > 0,
													receded: false,
													clickable: qty < held,
													rowAriaLabel: this.t(
														"storefront.orderAccess.handover.sheet.rowAriaLabel",
														{ type: spoken, count: held },
													),
													nameButtonAriaLabel: "",
													descriptionButtonAriaLabel: "",
													descriptionBackLabel: "",
													slot: {
														kind: "stepper",
														qty,
														canIncrement: qty < held,
														canDecrement: qty > 0,
														incrementAriaLabel: this.t(
															"storefront.orderAccess.handover.sheet.addAriaLabel",
															{ type: spoken },
														),
														decrementAriaLabel: this.t(
															"storefront.orderAccess.handover.sheet.removeAriaLabel",
															{ type: spoken },
														),
														// Ten taps is where tapping stops being a quantity
														// entry — the same threshold guest issuance types at.
														qtyField:
															held > 9
																? {
																		ariaLabel: this.t(
																			"storefront.orderAccess.handover.sheet.qtyAriaLabel",
																			{ type: spoken },
																		),
																	}
																: null,
													},
												};
												return html`
													<ticket-type-row
														.row=${row}
														@increment=${() => {
															if (this.sending !== null) return;
															this.picked = resolveGroupPick(
																this.picked,
																group.ticketIds,
																qty + 1,
															);
														}}
														@decrement=${() => {
															if (this.sending !== null) return;
															this.picked = resolveGroupPick(
																this.picked,
																group.ticketIds,
																qty - 1,
															);
														}}
														@qtyEntered=${(
															e: CustomEvent<{ value: string }>,
														) => {
															const typed = e.detail.value.trim();
															// A cleared field and a typed 0 are one intent —
															// nothing of this type goes.
															if (this.sending !== null) return;
															this.picked = resolveGroupPick(
																this.picked,
																group.ticketIds,
																typed === "" ? 0 : Number.parseInt(typed, 10),
															);
														}}
													></ticket-type-row>
												`;
											},
										)}
									</div>
								`;
							},
						)}
					</div>
				</schmancy-details>

				<div class="flex flex-col gap-2">

					<schmancy-typography type="label" token="md" class="text-surface-onVariant">
						${this.t("storefront.orderAccess.handover.sheet.channelLabel")}
					</schmancy-typography>
					<schmancy-button
						variant="outlined"
						color=${this.failed === "whatsapp" ? "error" : "neutral"}
						width="full"
						justify="start"
						?disabled=${!ready && this.sending !== "whatsapp"}
						.busy=${this.sending === "whatsapp"}
						@click=${() => this.submit("whatsapp", null)}
					>
						<schmancy-icon slot="prefix">chat</schmancy-icon>
						${this.t(this.failed === "whatsapp" ? "storefront.orderAccess.handover.sheet.retry" : "storefront.orderAccess.handover.sheet.whatsapp")}
					</schmancy-button>
					<!-- The address is asked for in an overlay of its own above this
					     sheet, never as a field unfolding among the exits: one
					     question, one field, one send, with nothing else on screen
					     competing for the tap that answers it. This sheet stands
					     underneath throughout, so an overlay the buyer dismisses
					     hands them back the name and the picks they already made and
					     the record-only exit to keep the handover with. -->
					<schmancy-button
						variant="outlined"
						color="neutral"
						width="full"
						justify="start"
						?disabled=${!ready}
						@click=${() => {
							if (!ready) return;
							show(
								() => html`
								<handover-email-address
									.recipient=${this.name.trim()}
									@send=${(e: CustomEvent<{ address: string; settle: (accepted: boolean, recorded: boolean) => void }>) => this.submit("email", e.detail.address, e.detail.settle)}
								></handover-email-address>
							`,
							)
								.pipe(takeUntil(this.disconnecting))
								.subscribe();
						}}
					>
						<schmancy-icon slot="prefix">mail</schmancy-icon>
						${this.t("storefront.orderAccess.handover.sheet.email")}
					</schmancy-button>
					${when(
						typeof navigator.share === "function",
						() => html`
							<schmancy-button
								variant="outlined"
								color=${this.failed === "share" ? "error" : "neutral"}
								width="full"
								justify="start"
								?disabled=${!ready && this.sending !== "share"}
								.busy=${this.sending === "share"}
								@click=${() => this.submit("share", null)}
							>
								<schmancy-icon slot="prefix">ios_share</schmancy-icon>
								${this.t(this.failed === "share" ? "storefront.orderAccess.handover.sheet.retry" : "storefront.orderAccess.handover.sheet.share")}
							</schmancy-button>
						`,
					)}
					<schmancy-button
						variant="text"
						color=${this.failed === "record" ? "error" : "neutral"}
						width="full"
						justify="start"
						?disabled=${!ready && this.sending !== "record"}
						.busy=${this.sending === "record"}
						@click=${() => this.submit(null, null)}
					>
						<schmancy-icon slot="prefix">handshake</schmancy-icon>
						${this.t(this.failed === "record" ? "storefront.orderAccess.handover.sheet.retry" : "storefront.orderAccess.handover.sheet.inPerson")}
					</schmancy-button>

				</div>

				${when(
					editing?.reclaimable === true ? editing.handoverId : null,
					(handoverId) => html`
						<schmancy-button
							variant="text"
							color="error"
							width="full"
							?disabled=${this.sending !== null && this.sending !== "reclaim"}
							.busy=${this.sending === "reclaim"}
							@click=${() => {
								if (this.sending !== null) return;
								this.pendingRows = handoverPickerRows.get();
								this.pendingNames = handoverNames.get();
								this.pendingEditing = editing ?? null;
								this.sending = "reclaim";
								this.failed = null;
								this.reclaim.next(handoverId);
							}}
						>
							${this.t(this.failed === "reclaim" ? "storefront.orderAccess.handover.sheet.retry" : "storefront.orderAccess.handover.sheet.reclaim")}
						</schmancy-button>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"handover-sheet": HandoverSheet;
	}
}
